// Shared subject + body composer with a live preview, used by both the template editor
// and the send dialog.
//
// The body editor is a compact TipTap rather than FeasibilityEditor/NarrativeEditor:
// that one carries Word import, tables and cell backgrounds (wrong tools for an email)
// and never exposes its editor instance, which the "insert variable at cursor" chips need.
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import { sfqApi } from "../../../api/feasibility";
import {
  TEMPLATE_VARS, REQUIRED_VAR, SUBJECT_MAX, hasRequiredVars,
  renderPreviewHtml, renderPreviewText, normalizeAssetUrls,
} from "./emailTemplates";

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$single ? "1fr" : "1fr 1fr")};
  gap: 16px;
  min-height: 0;
  flex: 1;
`;

const Pane = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const PaneTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 8px 0;
`;

const Chip = styled.button`
  padding: 2px 8px;
  border: 1px solid #b6d4fe;
  border-radius: 10px;
  background: #e7f1ff;
  color: #084298;
  font-size: 11px;
  cursor: pointer;
  &:hover { background: #d0e3ff; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 2px;
  padding: 4px;
  border: 1px solid #ced4da;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background: #f8f9fa;
`;

const TBtn = styled.button`
  min-width: 26px;
  padding: 3px 6px;
  border: 1px solid ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  border-radius: 3px;
  background: ${(p) => (p.$active ? "#e7f1ff" : "transparent")};
  color: #212529;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #e9ecef; }
`;

const EditorBox = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #ced4da;
  border-radius: 0 0 4px 4px;

  /* Typography and block spacing are the preview's, not this pane's: the operator is
     editing the mail, so an inline font-size in the body has to read against the same
     base here as it will there. Kept in step with PREVIEW_SHELL below. */
  .ProseMirror {
    min-height: 100%;
    padding: 10px 12px;
    font-family: -apple-system, "Segoe UI", Roboto, "Malgun Gothic", sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #212529;
    outline: none;
  }
  .ProseMirror p { margin: 0 0 10px; }
  .ProseMirror ul, .ProseMirror ol { margin: 0 0 10px; padding-left: 22px; }
  .ProseMirror blockquote { margin: 0 0 10px; padding-left: 12px; border-left: 3px solid #dee2e6; }
  .ProseMirror a { color: #0d6efd; word-break: break-all; }
  .ProseMirror hr { border: none; border-top: 1px solid #dee2e6; margin: 16px 0; }
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    display: block;
    border-radius: 2px;
  }
  .ProseMirror img.ProseMirror-selectednode { outline: 2px solid #0d6efd; }
`;

const PreviewFrame = styled.iframe`
  flex: 1;
  min-height: 0;
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 0 0 4px 4px;
  background: #fff;
`;

const PreviewSubject = styled.div`
  padding: 7px 10px;
  border: 1px solid #ced4da;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background: #f8f9fa;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Warn = styled.div`
  margin-top: 8px;
  padding: 7px 10px;
  border-radius: 4px;
  background: #fff5f5;
  border-left: 3px solid #dc3545;
  font-size: 12px;
  color: #842029;
`;

const Note = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #868e96;
`;

const Uploading = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: ${(p) => (p.$error ? "#c92a2a" : "#0d6efd")};
`;

// The preview is an isolated document: it renders like a mail client rather than
// inheriting the app's CSS, and with no allow-scripts it cannot execute anything
// an operator (or a bad template) put in the body.
// Kept in step with asEmailDocument/BLOCK_STYLE in the API's services/Feasibility/email.js,
// which inlines these same values onto the outgoing mail — that is what makes the preview
// a promise rather than an approximation. The :empty rule stands in for the &nbsp; the
// send path writes into a blank paragraph so mail clients cannot collapse it away.
const PREVIEW_SHELL = (bodyHtml) => `<!doctype html>
<html><head><meta charset="utf-8">
<style>
  body { margin:0; padding:14px; font-family:-apple-system,"Segoe UI",Roboto,"Malgun Gothic",sans-serif;
         font-size:14px; line-height:1.6; color:#212529; }
  p { margin:0 0 10px; } p:empty::after { content:"\\00a0"; }
  ul, ol { margin:0 0 10px; padding-left:22px; }
  blockquote { margin:0 0 10px; padding-left:12px; border-left:3px solid #dee2e6; }
  a { color:#0d6efd; word-break:break-all; } hr { border:none; border-top:1px solid #dee2e6; margin:16px 0; }
  img { max-width:100%; height:auto; }
</style></head><body>${bodyHtml}</body></html>`;

// Embedded images are served by the API behind the session cookie, and a bare sandbox=""
// gives the frame an opaque origin — which drops that cookie and leaves every image
// broken. allow-same-origin restores it; scripts stay blocked, which is what the sandbox
// was here for.
const PREVIEW_SANDBOX = "allow-same-origin";

// Types the API accepts. Mirrors ALLOWED in services/Feasibility/emailAsset.js — SVG is
// excluded there because it can carry script.
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const IMAGE_ACCEPT = IMAGE_TYPES.join(",");

// Mail bodies are read in a ~600px column; a 4000px phone photo inserted at natural size
// would blow the layout out in every client. Outlook ignores CSS max-width, so the cap
// has to be a real width attribute on the tag.
const MAX_IMAGE_WIDTH = 560;

// The rendered width to write onto the tag, read from the file before it is uploaded.
// Falls back to the cap if the image cannot be decoded — the API has already agreed it
// is a real image by then, so this is only about picking a number.
function measureWidth(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(Math.min(img.naturalWidth || MAX_IMAGE_WIDTH, MAX_IMAGE_WIDTH));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(MAX_IMAGE_WIDTH);
    };
    img.src = url;
  });
}

// Inline styles on blocks — the password box, the small grey confidentiality footer,
// anything an operator pasted in with its own formatting.
//
// TipTap keeps only the attributes an extension declares, so `style` was parsed away:
// the editor showed the password line as plain text while the preview (rendered from
// the stored HTML) still showed the box, and the first keystroke pushed the stripped
// HTML up as the new body — the formatting then vanished from the preview and from the
// mail. The API deliberately preserves these on send (withBlockSpacing in the API's
// services/Feasibility/email.js puts its own spacing *behind* the operator's rule), so
// the editor was the only place the body lost its formatting.
const STYLED_BLOCKS = [
  "paragraph", "heading", "bulletList", "orderedList", "listItem", "blockquote", "horizontalRule",
];

// Typography only. A declaration that can fetch or execute has no business in a mail
// body, and the API strips scripts but not the style attribute — so filter here rather
// than let one through to the preview iframe and the recipient's client.
const UNSAFE_STYLE_RE = /(expression|javascript:|url\s*\()/i;
const safeStyle = (value) => {
  const style = String(value || "").trim();
  return style && !UNSAFE_STYLE_RE.test(style) ? style : null;
};

const BlockStyle = Extension.create({
  name: "blockStyle",
  addGlobalAttributes() {
    return [{
      types: STYLED_BLOCKS,
      attributes: {
        style: {
          default: null,
          parseHTML: (el) => safeStyle(el.getAttribute("style")),
          renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
        },
      },
    }];
  },
});

// TipTap's Image drops unknown attributes on serialisation, so width has to be declared
// to survive the round trip through the stored HTML.
const EmailImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
    };
  },
});

// Consecutive keystrokes inside this window collapse into one undo step, so Ctrl+Z
// rolls back a word rather than a single character.
const UNDO_COALESCE_MS = 600;

// Undo/redo for the subject field.
//
// The body gets this free from TipTap, but the subject is a controlled <input>: React
// reassigns .value on every render, and a programmatic edit (a variable chip) wipes the
// browser's native undo stack — so Ctrl+Z did nothing there. Keep our own snapshots.
function useUndoableSubject(subject, setSubject, inputRef) {
  const snap = (value) => ({ value: value || "", caret: (value || "").length });
  const hist = useRef({ entries: [snap(subject)], idx: 0, at: 0 });
  // The last value *we* pushed to the parent, used to tell our own edits apart from an
  // external replacement (language switch, reset-to-template), which starts a fresh history.
  const mine = useRef(subject || "");

  useEffect(() => {
    if ((subject || "") !== mine.current) {
      mine.current = subject || "";
      hist.current = { entries: [snap(subject)], idx: 0, at: 0 };
    }
  }, [subject]);

  const apply = (entry) => {
    mine.current = entry.value;
    setSubject(entry.value);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(entry.caret, entry.caret);
    });
  };

  // discrete: a variable insertion, which always deserves its own undo step.
  const record = (value, caret, discrete = false) => {
    const h = hist.current;
    const now = Date.now();
    h.entries = h.entries.slice(0, h.idx + 1);   // a fresh edit drops the redo tail
    const entry = { value: value || "", caret };

    if (!discrete && now - h.at < UNDO_COALESCE_MS && h.idx > 0) {
      h.entries[h.idx] = entry;                  // still mid-burst: fold into the top step
    } else {
      h.entries.push(entry);
      h.idx = h.entries.length - 1;
    }
    // A discrete step must not absorb the typing that follows it.
    h.at = discrete ? 0 : now;
    mine.current = entry.value;
  };

  const undo = () => {
    const h = hist.current;
    if (h.idx > 0) apply(h.entries[--h.idx]);
  };
  const redo = () => {
    const h = hist.current;
    if (h.idx < h.entries.length - 1) apply(h.entries[++h.idx]);
  };

  const onKeyDown = (e) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === "z") {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    } else if (k === "y") {
      e.preventDefault();
      redo();
    }
  };

  return { record, onKeyDown };
}

// The 개인정보 동의문 editor (ConsentTemplateModal) reuses this composer as-is: it is the
// same job — rich text authored by an operator, with images hosted by this API. What
// differs is only the trim, so the props below default to the mail behaviour and the
// consent editor turns them off: no {{variables}} to insert, no questionnaire link to
// require, its own field label, and no preview.
//
// The preview pane earns its place on a mail and only on a mail: it is the one view that
// resolves {{piName}} and friends to real values and masks {{password}}, none of which
// the editing pane can show. A consent notice has no variables, so the preview would be
// a second copy of the pane beside it — and a misleading one, since the site reads that
// notice under the app's own styles (narrativeViewerStyles in SiteConsentGate), not the
// mail shell below.
export default function EmailComposer({
  sfqId, subject, bodyHtml, onChange, vars,
  varList = TEMPLATE_VARS,
  requireLink = true,
  subjectLabel = "제목 (Subject)",
  note = null,
  showPreview = true,
}) {
  // A variable chip inserts into whichever field the operator was last editing, so the
  // same chips serve both the subject and the body. Body is the default on first open.
  const [target, setTarget] = useState("body");
  const subjectRef = useRef(null);
  const fileRef = useRef(null);
  const [upload, setUpload] = useState(null);   // { busy } | { error } | null
  // Where the caret was when the subject input lost focus to the chip — reading
  // selectionStart at click time would be too late, the input is already blurred.
  const subjectCaret = useRef(null);

  const setSubject = (value) => onChange({ subject: value });
  const subjectUndo = useUndoableSubject(subject, setSubject, subjectRef);

  // Paste and drop reach the editor before `insertImage` below exists, and capturing it
  // in editorProps would freeze the first render's closure. The ref is reassigned every
  // render, so the handlers always call the current one.
  const insertImageRef = useRef(null);

  // Pulls image files out of a paste/drop. Returning true from the handler tells
  // ProseMirror we took the event — otherwise it would also insert whatever HTML the
  // clipboard carried alongside the file (Word pastes both).
  const handleImageEvent = (files) => {
    const images = [...(files || [])].filter((f) => IMAGE_TYPES.includes(f.type));
    if (!images.length) return false;
    images.forEach((f) => insertImageRef.current?.(f));
    return true;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      TextStyle,
      Color,
      // allowBase64 stays off: a base64 <img> would be stored inline in the template and
      // stripped by the API anyway (services/Feasibility/emailAsset.js). Uploading is the
      // only way an image gets in.
      EmailImage.configure({ inline: false, allowBase64: false }),
      BlockStyle,
    ],
    content: normalizeAssetUrls(bodyHtml || ""),
    onUpdate: ({ editor: ed }) => onChange({ bodyHtml: ed.getHTML() }),
    editorProps: {
      handlePaste: (view, event) => handleImageEvent(event.clipboardData?.files),
      handleDrop:  (view, event) => handleImageEvent(event.dataTransfer?.files),
    },
  });

  // The parent swaps the whole body when the language changes or the template is reset.
  // Guard on equality: without it, every keystroke's onChange would round-trip back in
  // here and reset the cursor to the top of the document. Both sides are normalized so a
  // template stored against a different API host does not read as a change on every pass.
  //
  // Parsing is lossy in small ways TipTap decides (<b> becomes <strong>, a stray <br>
  // between paragraphs becomes an empty one, rel/target are added to links), so the HTML
  // the editor holds is not always the HTML it was handed. The preview is rendered from
  // the parent's copy, so leaving the two different meant the preview showed the stored
  // body until the first keystroke replaced it with the editor's — the body appearing to
  // change on its own. Adopt what the editor parsed as the body: from here on the
  // preview, the saved template and the sent mail are all exactly what is on screen.
  useEffect(() => {
    if (!editor) return;
    const next = normalizeAssetUrls(bodyHtml || "");
    if (next === editor.getHTML()) return;
    editor.commands.setContent(next, { emitUpdate: false });
    const parsed = editor.getHTML();
    if (parsed !== next) onChange({ bodyHtml: parsed });
  }, [bodyHtml, editor]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Upload first, insert second: the tag must reference a stored asset, never a blob URL
  // that dies with the page or a base64 blob the API would strip.
  const insertImage = async (file) => {
    if (!editor) return;
    if (!sfqId) {
      setUpload({ error: "이미지를 올릴 스터디를 확인할 수 없습니다." });
      return;
    }
    setUpload({ busy: true });
    try {
      const [res, width] = await Promise.all([
        sfqApi.uploadEmailAsset(sfqId, file),
        measureWidth(file),
      ]);
      const asset = res.data?.data;
      editor.chain().focus().setImage({
        src: sfqApi.emailAssetUrl(sfqId, asset.id),
        alt: asset.fileName,
        width,
      }).run();
      setUpload(null);
    } catch (err) {
      setUpload({ error: err?.response?.data?.error?.message || "이미지 업로드에 실패했습니다." });
    }
  };
  insertImageRef.current = insertImage;

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    // Reset first: picking the same file twice in a row fires no change event otherwise.
    e.target.value = "";
    if (file) insertImage(file);
  };

  const insertVar = (key) => {
    const token = `{{${key}}}`;

    if (target === "subject") {
      const el  = subjectRef.current;
      const cur = subject || "";
      const at  = subjectCaret.current ?? cur.length;
      const pos = Math.min(at, cur.length);
      const value = (cur.slice(0, pos) + token + cur.slice(pos)).slice(0, SUBJECT_MAX);
      // Put the caret back after the token instead of dumping it at the end.
      const next = Math.min(pos + token.length, value.length);
      subjectUndo.record(value, next, true);   // its own undo step
      onChange({ subject: value });
      subjectCaret.current = next;
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        el.setSelectionRange(next, next);
      });
      return;
    }

    if (editor) editor.chain().focus().insertContent(token).run();
  };

  const rememberCaret = () => {
    subjectCaret.current = subjectRef.current?.selectionStart ?? null;
  };

  const preview = useMemo(() => (showPreview ? {
    subject: renderPreviewText(subject, vars),
    body:    renderPreviewHtml(normalizeAssetUrls(bodyHtml), vars),
  } : null), [subject, bodyHtml, vars, showPreview]);

  const linkMissing = requireLink && !hasRequiredVars(bodyHtml);

  const handleLink = () => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("URL을 입력하세요:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <>
      <Label htmlFor="mail-subject">{subjectLabel}</Label>
      <Input
        id="mail-subject"
        ref={subjectRef}
        value={subject}
        maxLength={SUBJECT_MAX}
        onChange={(e) => {
          subjectUndo.record(e.target.value, e.target.selectionStart);
          onChange({ subject: e.target.value });
        }}
        onFocus={() => setTarget("subject")}
        onKeyDown={subjectUndo.onKeyDown}
        onSelect={rememberCaret}
        onKeyUp={rememberCaret}
        onBlur={rememberCaret}
        placeholder="[CRO] Site Feasibility Questionnaire - {{siteName}}"
      />

      {varList.length > 0 && (
      <Chips>
        <span style={{ fontSize: 11, color: "#868e96", alignSelf: "center", marginRight: 2 }}>
          변수 삽입 → <b>{target === "subject" ? "제목" : "본문"}</b>
        </span>
        {varList.map((v) => (
          <Chip
            key={v.key}
            type="button"
            // The chip must not steal focus: blurring the subject input first would lose
            // the caret, and blurring the editor would drop its selection.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertVar(v.key)}
            disabled={!editor}
            title={`{{${v.key}}} — ${v.label}`}
          >
            {v.label}
          </Chip>
        ))}
      </Chips>
      )}

      <Grid $single={!showPreview}>
        <Pane>
          <PaneTitle>본문 편집</PaneTitle>
          <Toolbar>
            <TBtn type="button" $active={editor?.isActive("bold")}
                  onClick={() => editor?.chain().focus().toggleBold().run()}><b>B</b></TBtn>
            <TBtn type="button" $active={editor?.isActive("italic")}
                  onClick={() => editor?.chain().focus().toggleItalic().run()}><i>I</i></TBtn>
            <TBtn type="button" $active={editor?.isActive("underline")}
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}><u>U</u></TBtn>
            <TBtn type="button" $active={editor?.isActive("bulletList")}
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}>• 목록</TBtn>
            <TBtn type="button" $active={editor?.isActive("orderedList")}
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1. 목록</TBtn>
            <TBtn type="button" $active={editor?.isActive("link")} onClick={handleLink}>🔗</TBtn>
            <TBtn type="button" onClick={() => editor?.chain().focus().setHorizontalRule().run()}>—</TBtn>
            <TBtn
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={!editor || upload?.busy}
              title="이미지 삽입 (붙여넣기·끌어놓기도 가능)"
            >
              🖼
            </TBtn>
          </Toolbar>
          <input
            ref={fileRef}
            type="file"
            accept={IMAGE_ACCEPT}
            style={{ display: "none" }}
            onChange={handlePickImage}
          />
          <EditorBox onFocus={() => setTarget("body")}>
            <EditorContent editor={editor} />
          </EditorBox>
          {upload?.busy && <Uploading>이미지 업로드 중...</Uploading>}
          {upload?.error && <Uploading $error>{upload.error}</Uploading>}
        </Pane>

        {preview && (
          <Pane>
            <PaneTitle>미리보기</PaneTitle>
            <PreviewSubject title={preview.subject}>{preview.subject || "(제목 없음)"}</PreviewSubject>
            <PreviewFrame sandbox={PREVIEW_SANDBOX} title="email preview" srcDoc={PREVIEW_SHELL(preview.body)} />
          </Pane>
        )}
      </Grid>

      {linkMissing && (
        <Warn>
          본문에 설문 링크 변수 <b>{`{{${REQUIRED_VAR}}}`}</b>가 없습니다.
          링크가 없으면 수신자가 설문에 접속할 수 없습니다.
        </Warn>
      )}
      <Note>
        {note || (
          <>
            비밀번호는 발송 시점에 새로 생성되므로 미리보기에는 <b>••••••••</b>로 표시됩니다.
            <br />
            이미지는 메일에 함께 첨부되어 발송되므로 수신자가 별도 접속 없이 볼 수 있습니다
            (PNG·JPG·GIF·WebP, 장당 2MB 이하).
          </>
        )}
      </Note>
    </>
  );
}
